import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import debounce from "lodash/debounce";
import {
  getShippingSchedules,
  getCompletedShippingSchedules,
  createShippingSchedule,
  updateShippingSchedule,
  completeShippingSchedule,
  reorderShippingSchedules,
} from "../api/customerShippingScheduleApi";
import { PencilIcon } from "lucide-react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import toast from "react-hot-toast";

const DraggableRow = ({ row, rowId }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rowId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || "none",
    opacity: isDragging ? 0.8 : 1,
    background: isDragging ? "#eff6ff" : undefined,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className="group border-b hover:bg-blue-50/40"
      {...attributes}
      {...listeners}
    >
      {row.getVisibleCells().map((cell) => (
        <td
          key={cell.id}
          className="border-r border-gray-200 last:border-r-0 p-0"
          style={{
            width: cell.column.getSize(),
            minWidth: cell.column.getSize(),
            maxWidth: cell.column.getSize(),
          }}
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </td>
      ))}
    </tr>
  );
};

const CustomerShippingSchedule = () => {
  const [data, setData] = useState([]);
  const [activeTab, setActiveTab] = useState("active");
  const [activeData, setActiveData] = useState([]);
  const [completedData, setCompletedData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedShipmentId, setSelectedShipmentId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [activeRows, completedRows] = await Promise.all([
          getShippingSchedules(),
          getCompletedShippingSchedules(),
        ]);

        setActiveData(activeRows.sort((a, b) => a.sequence - b.sequence));
        setCompletedData(
          completedRows.sort(
            (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt),
          ),
        );
      } catch (err) {
        setError("Failed to load shipping schedule");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const debouncedUpdate = useCallback(
    debounce((id, field, value) => {
      updateShippingSchedule(id, { [field]: value }).catch(console.error);
    }, 500),
    [],
  );

  const paginatedCompletedData = useMemo(() => {
    if (activeTab !== "completed") return [];

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return completedData.slice(startIndex, endIndex);
  }, [completedData, currentPage, activeTab]);

  const totalCompletedPages = Math.ceil(completedData.length / itemsPerPage);

  useEffect(() => {
    if (activeTab === "completed") {
      setCurrentPage(1);
    }
  }, [activeTab, completedData.length]);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalCompletedPages) return;
    setCurrentPage(page);
  };

  const handleCellChange = useCallback(
    (rowIndex, columnId, value) => {
      setActiveData((old) =>
        old.map((row, i) =>
          i === rowIndex ? { ...row, [columnId]: value } : row,
        ),
      );
      const rowData = activeData[rowIndex];
      if (rowData?._id) {
        debouncedUpdate(rowData._id, columnId, value);
      }
    },
    [activeData, debouncedUpdate],
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setActiveData((old) => {
      const oldIndex = old.findIndex((r) => r._id === active.id);
      const newIndex = old.findIndex((r) => r._id === over.id);
      if (oldIndex === -1 || newIndex === -1) return old;

      const newData = arrayMove(old, oldIndex, newIndex);
      newData.forEach((item, i) => (item.sequence = i + 1));
      reorderShippingSchedules(newData.map((r) => r._id)).catch(console.error);
      return newData;
    });
  };

  const handleComplete = (id) => {
    setSelectedShipmentId(id);
    setShowConfirmModal(true);
  };

  const handleAddRow = async () => {
    const emptyRow = {
      shipDate: "TBC",
      customerOrder: "",
      pickupOrShip: "",
      notes: "",
      paymentReceived: "",
      readyForShipping: false,
      palletOrBoxInfo: "",
      plInErpOrPag: "",
      transportBookingStatus: "",
      shipped: false,
      papersReceived: false,
      invoiceSent: false,
    };
    try {
      const created = await createShippingSchedule(emptyRow);
      setActiveData((prev) => [...prev, created]);
    } catch {
      toast.error("Failed to create new entry");
    }
  };

  const ReadOnlyTextCell = ({ getValue }) => {
    const value = getValue() ?? "";
    return (
      <div className="px-3 py-2.5 min-h-[42px] flex items-center text-sm text-gray-700">
        {value || "—"}
      </div>
    );
  };

  const ReadOnlyCheckboxCell = ({ getValue }) => (
    <div className="flex items-center justify-center h-full p-3">
      <input
        type="checkbox"
        checked={!!getValue()}
        readOnly
        disabled
        className="h-4 w-4 text-gray-400 bg-gray-100 cursor-not-allowed"
      />
    </div>
  );

  const CheckboxCell = ({ getValue, row, column, table }) => (
    <div className="flex items-center justify-center h-full p-3">
      <input
        type="checkbox"
        checked={!!getValue()}
        onChange={(e) => {
          handleCellChange(row.index, column.id, e.target.checked);
        }}
        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );

  const EditableCell = ({ getValue, row, column, table }) => {
    const initialValue = getValue() ?? "";
    const [value, setValue] = useState(initialValue);
    const [isEditing, setIsEditing] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
      setValue(initialValue);
    }, [initialValue]);

    const handleSave = () => {
      const finalValue = value.trim();
      if (finalValue !== initialValue?.toString().trim()) {
        handleCellChange(row.index, column.id, finalValue || "");
      }
      setIsEditing(false);
    };

    const stopKeyboardPropagation = (e) => {
      e.stopPropagation();
    };

    if (!isEditing) {
      return (
        <div
          onDoubleClick={() => setIsEditing(true)}
          className={`
          px-3 py-2.5 min-h-[42px] flex items-center cursor-pointer
          text-sm text-gray-800 group-hover:text-blue-800 select-text
          ${!value ? "text-gray-400 italic" : ""}
        `}
          title="Double-click to edit"
        >
          <span>{value || "—"}</span>
          <PencilIcon className="ml-auto w-3.5 h-3.5 opacity-0 group-hover:opacity-70 text-blue-500 flex-shrink-0" />
        </div>
      );
    }

    return (
      <div className="relative">
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={(e) => {
            stopKeyboardPropagation(e);

            if (e.key === "Enter") {
              e.preventDefault();
              handleSave();
            }
            if (e.key === "Escape") {
              setValue(initialValue);
              setIsEditing(false);
            }
          }}
          autoFocus
          className="w-full px-3 py-2 text-sm border-2 border-blue-400 rounded focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-200/50 bg-white"
          onMouseDown={(e) => e.stopPropagation()}
        />
      </div>
    );
  };

  const activeColumns = useMemo(
    () => [
      {
        header: "SHIP DATE",
        accessorKey: "shipDate",
        size: 110,
        cell: EditableCell,
      },
      {
        header: "CUSTOMER ORDER",
        accessorKey: "customerOrder",
        size: 260,
        cell: EditableCell,
      },
      {
        header: "PICKUP\nOR SHIP",
        accessorKey: "pickupOrShip",
        size: 140,
        cell: EditableCell,
      },
      {
        header: "NOTES / PROGRESS",
        accessorKey: "notes",
        size: 360,
        cell: EditableCell,
      },
      {
        header: "PAYMENT\nRECEIVED",
        accessorKey: "paymentReceived",
        size: 150,
        cell: EditableCell,
      },
      {
        header: "READY FOR\nSHIPPING",
        accessorKey: "readyForShipping",
        size: 100,
        cell: CheckboxCell,
      },
      {
        header: "PALLET / BOX INFO",
        accessorKey: "palletOrBoxInfo",
        size: 180,
        cell: EditableCell,
      },
      {
        header: "PL IN\nERPAG",
        accessorKey: "plInErpOrPag",
        size: 110,
        cell: EditableCell,
      },
      {
        header: "TRANSPORT BOOKING\nSTATUS",
        accessorKey: "transportBookingStatus",
        size: 220,
        cell: EditableCell,
      },
      {
        header: "Shipped?",
        accessorKey: "shipped",
        size: 90,
        cell: CheckboxCell,
      },
      {
        header: "Papers\nreceived",
        accessorKey: "papersReceived",
        size: 90,
        cell: CheckboxCell,
      },
      {
        header: "Invoice\nsent",
        accessorKey: "invoiceSent",
        size: 90,
        cell: CheckboxCell,
      },
      {
        id: "actions",
        header: "Actions",
        size: 140,
        cell: ({ row }) => (
          <div className="flex items-center justify-center h-full px-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleComplete(row.original._id);
              }}
              disabled={!row.original._id}
              className="px-3.5 py-1.5 bg-[#6ca351] hover:bg-green-700 active:bg-green-800 disabled:bg-gray-400 text-white text-xs font-medium rounded shadow-sm transition-all whitespace-nowrap"
            >
              Complete
            </button>
          </div>
        ),
      },
    ],
    [handleCellChange, handleComplete],
  );

  const completedColumns = useMemo(
    () => [
      {
        header: "SHIP DATE",
        accessorKey: "shipDate",
        size: 110,
        cell: ReadOnlyTextCell,
      },
      {
        header: "CUSTOMER ORDER",
        accessorKey: "customerOrder",
        size: 260,
        cell: ReadOnlyTextCell,
      },
      {
        header: "PICKUP\nOR SHIP",
        accessorKey: "pickupOrShip",
        size: 140,
        cell: ReadOnlyTextCell,
      },
      {
        header: "NOTES / PROGRESS",
        accessorKey: "notes",
        size: 360,
        cell: ReadOnlyTextCell,
      },
      {
        header: "PAYMENT\nRECEIVED",
        accessorKey: "paymentReceived",
        size: 150,
        cell: ReadOnlyTextCell,
      },
      {
        header: "READY FOR\nSHIPPING",
        accessorKey: "readyForShipping",
        size: 100,
        cell: ReadOnlyCheckboxCell,
      },
      {
        header: "PALLET / BOX INFO",
        accessorKey: "palletOrBoxInfo",
        size: 180,
        cell: ReadOnlyTextCell,
      },
      {
        header: "PL IN\nERPAG",
        accessorKey: "plInErpOrPag",
        size: 110,
        cell: ReadOnlyTextCell,
      },
      {
        header: "TRANSPORT BOOKING\nSTATUS",
        accessorKey: "transportBookingStatus",
        size: 220,
        cell: ReadOnlyTextCell,
      },
      {
        header: "Shipped?",
        accessorKey: "shipped",
        size: 90,
        cell: ReadOnlyCheckboxCell,
      },
      {
        header: "Papers\nreceived",
        accessorKey: "papersReceived",
        size: 90,
        cell: ReadOnlyCheckboxCell,
      },
      {
        header: "Invoice\nsent",
        accessorKey: "invoiceSent",
        size: 90,
        cell: ReadOnlyCheckboxCell,
      },
      {
        id: "status",
        header: "Status",
        size: 140,
        cell: () => (
          <div className="text-center text-xs font-semibold text-green-700">
            Completed
          </div>
        ),
      },
    ],
    [],
  );

  const generateShippingSchedulePDF = useCallback(() => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    const margin = 10;
    const pageWidth = doc.internal.pageSize.getWidth();
    let startY = 18;

    // Title & Date
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Customer Shipping Schedule", margin, startY);

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    const today = new Date().toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    doc.text(today, pageWidth - margin, startY, { align: "right" });
    startY += 10;

    // Headers (no \n, clean)
    const headers = [
      "Ship Date",
      "Customer Order",
      "Pickup or Ship",
      "Notes / Progress",
      "Payment Received",
      "Ready",
      "Pallet / Box",
      "PL in ERPAG",
      "Transport Booking",
      "Shipped?",
      "Papers Rec.",
      "Invoice Sent",
    ];

    const body = activeData.map((row) => [
      row.shipDate || "TBC",
      row.customerOrder || "—",
      row.pickupOrShip || "—",
      row.notes || "",
      row.paymentReceived || "—",
      row.readyForShipping ? "YES" : "no",
      row.palletOrBoxInfo || "—",
      row.plInErpOrPag || "—",
      row.transportBookingStatus || "—",
      row.shipped ? "YES" : "no",
      row.papersReceived ? "YES" : "no",
      row.invoiceSent ? "YES" : "no",
    ]);

    autoTable(doc, {
      startY,
      head: [headers],
      body,
      theme: "grid",
      styles: {
        fontSize: 7,
        cellPadding: 1.8,
        overflow: "linebreak",
        lineWidth: 0.1,
        minCellHeight: 4,
      },
      headStyles: {
        fillColor: [0, 0, 0],
        textColor: 255,
        fontSize: 7.5,
        fontStyle: "bold",
        halign: "center",
        valign: "middle",
      },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      margin: { top: startY + 3, left: margin, right: margin },

      horizontalPageBreak: false,

      columnStyles: {
        0: { cellWidth: 20 },
        1: { cellWidth: 32 },
        2: { cellWidth: 20 },
        3: { cellWidth: 48 },
        4: { cellWidth: 21 },
        5: { cellWidth: 16 },
        6: { cellWidth: 26 },
        7: { cellWidth: 20 },
        8: { cellWidth: 32 },
        9: { cellWidth: 16 },
        10: { cellWidth: 16 },
        11: { cellWidth: 16 },
      },

      didParseCell: (data) => {
        const yesNoCols = [5, 9, 10, 11];
        if (yesNoCols.includes(data.column.index)) {
          data.cell.styles.halign = "center";
          if (data.cell.text[0] === "YES") {
            data.cell.styles.textColor = [0, 0, 0];
            data.cell.styles.fontStyle = "bold";
          } else if (data.cell.text[0] === "no") {
            data.cell.styles.textColor = [239, 68, 68];
          }
        }
        if (data.column.index === 3 && data.cell.text[0]?.length > 80) {
          data.cell.text = doc.splitTextToSize(data.cell.text[0], 48);
        }
      },

      didDrawPage: () => {
        doc.setFontSize(8);
        doc.text(
          `Printed ${today}`,
          pageWidth - margin,
          doc.internal.pageSize.height - 8,
          { align: "right" },
        );
      },
    });

    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(8);
    doc.setFont("helvetica", "italic");
    doc.text("Printed from Customer Shipping System", margin, finalY);

    window.open(doc.output("bloburl"), "_blank");
    toast.success("PDF generated (single page)");
  }, [activeData]);

  const table = useReactTable({
    data: activeTab === "active" ? activeData : paginatedCompletedData,
    columns: activeTab === "active" ? activeColumns : completedColumns,
    getCoreRowModel: getCoreRowModel(),
    meta: {
      updateData: handleCellChange,
    },
    columnResizeMode: "onChange",
    defaultColumn: {
      size: 120,
      minSize: 50,
      maxSize: 500,
    },
  });

  if (loading)
    return (
      // <div className="p-12 text-center text-gray-500">Loading schedule...</div>
      <div className="flex flex-col items-center justify-center p-12 space-y-4">
  {/* Spinner */}
  <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>

  {/* Text */}
  <p className="text-gray-600 text-sm font-medium animate-pulse">
    Loading schedule...
  </p>
</div>
    );
  if (error)
    return <div className="p-12 text-center text-red-600">{error}</div>;

  return (
    <div className="flex flex-col h-full bg-gray-50 min-h-screen">
      <div className="flex items-center justify-between px-6 py-4 border-b bg-white shadow-sm">
        <h1 className="text-xl font-semibold text-gray-800">
          Customer Shipping Schedule
        </h1>
        <div className="flex gap-3">
          {activeTab === "active" && (
            <button
              onClick={generateShippingSchedulePDF}
              className="px-5 py-2 bg-[#365daf] hover:bg-blue-900 text-white font-medium rounded-lg shadow transition-colors flex items-center gap-2"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                />
              </svg>
              Print / Export
            </button>
          )}

          {activeTab === "active" && (
            <button
              onClick={handleAddRow}
              className="px-5 py-2 bg-black hover:bg-gray-700 active:bg-gray-800 text-white font-medium rounded-lg shadow transition-colors"
              disabled={loading}
            >
              + Add New Row
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b bg-white px-6">
        <button
          onClick={() => setActiveTab("active")}
          className={`px-6 py-3 font-medium border-b-2 ${
            activeTab === "active"
              ? "border-gray-800 text-black"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          Active
        </button>
        <button
          onClick={() => setActiveTab("completed")}
          className={`px-6 py-3 font-medium border-b-2 ${
            activeTab === "completed"
              ? "border-gray-800 text-black"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          Completed
        </button>
      </div>

      <div className="flex-1 overflow-hidden">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          {activeTab === "active" ? (
            <div className="flex flex-col h-full">
              <div className="flex-1 overflow-auto">
                <div className="inline-block min-w-full align-top px-4 py-6">
                  <table className="w-full table-fixed border-collapse text-sm text-gray-700">
                    {/* thead */}
                    <thead className="sticky top-0 z-20 bg-[#4472c4] text-white shadow">
                      {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                          {headerGroup.headers.map((header) => (
                            <th
                              key={header.id}
                              className="py-3 px-4 bg-gray-200 text-gray-900 font-bold uppercase text-xs border-b-4 border-gray-700"
                              style={{
                                width: header.getSize(),
                              }}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <SortableContext
                        items={activeData.map((r) => r._id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {table.getRowModel().rows.map((row) => (
                          <DraggableRow
                            key={row.original._id}
                            row={row}
                            rowId={row.original._id}
                          />
                        ))}
                      </SortableContext>

                      {activeData.length === 0 && !loading && (
                        <tr>
                          <td
                            colSpan={activeColumns.length}
                            className="py-16 text-center text-gray-500 italic bg-gray-50"
                          >
                            No active shipments yet — click "Add New Row" to
                            begin
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col h-full">
              <div className="flex-1 overflow-auto">
                <div className="inline-block min-w-full align-top px-4 py-6">
                  <table className="w-full table-fixed border-collapse text-sm text-gray-700">
                    {/* thead */}
                    <thead className="sticky top-0 z-20 bg-gray-600 text-white shadow">
                      {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                          {headerGroup.headers.map((header) => (
                            <th
                              key={header.id}
                              className="py-3 px-4 bg-gray-200 text-gray-900 font-bold uppercase text-xs border-b-4 border-gray-700"
                              style={{ width: header.getSize() }}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>

                    <tbody className="bg-white divide-y divide-gray-200">
                      {table.getRowModel().rows.map((row) => (
                        <tr key={row.id} className="hover:bg-gray-50">
                          {row.getVisibleCells().map((cell) => (
                            <td
                              key={cell.id}
                              className="border-r border-gray-200 last:border-r-0 p-0"
                              style={{
                                width: cell.column.getSize(),
                                minWidth: cell.column.getSize(),
                              }}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}

                      {paginatedCompletedData.length === 0 && !loading && (
                        <tr>
                          <td
                            colSpan={completedColumns.length}
                            className="py-16 text-center text-gray-500 italic bg-gray-50"
                          >
                            No completed shipments yet
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {completedData.length > 0 && (
                <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200">
                  <div className="text-sm text-gray-700"></div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1.5 border rounded disabled:opacity-50 hover:bg-gray-50"
                    >
                      Previous
                    </button>

                    {/* Page numbers or ellipsis logic */}
                    {totalCompletedPages <= 8 ? (
                      Array.from(
                        { length: totalCompletedPages },
                        (_, i) => i + 1,
                      ).map((p) => (
                        <button
                          key={p}
                          onClick={() => handlePageChange(p)}
                          className={`px-3 py-1.5 border rounded-full min-w-[2.5rem] ${
                            currentPage === p
                              ? "bg-black text-white border-black"
                              : "text-gray-700 hover:bg-gray-100"
                          }`}
                        >
                          {p}
                        </button>
                      ))
                    ) : (
                      <>
                        <button
                          onClick={() => handlePageChange(1)}
                          className="px-3 py-1.5 border rounded hover:bg-gray-50"
                        >
                          1
                        </button>
                        {currentPage > 3 && <span className="px-2">...</span>}
                        {currentPage > 2 && (
                          <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            className="px-3 py-1.5 border rounded hover:bg-gray-50"
                          >
                            {currentPage - 1}
                          </button>
                        )}
                        <button className="w-9 h-9 flex items-center justify-center text-sm font-medium bg-black text-white border border-black rounded-full">
                          {currentPage}
                        </button>
                        {currentPage < totalCompletedPages - 1 && (
                          <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            className="px-3 py-1.5 border rounded hover:bg-gray-50"
                          >
                            {currentPage + 1}
                          </button>
                        )}
                        {currentPage < totalCompletedPages - 2 && (
                          <span className="px-2">...</span>
                        )}
                        <button
                          onClick={() => handlePageChange(totalCompletedPages)}
                          className="px-3 py-1.5 border rounded hover:bg-gray-50"
                        >
                          {totalCompletedPages}
                        </button>
                      </>
                    )}

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalCompletedPages}
                      className="px-3 py-1.5 border rounded disabled:opacity-50 hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DndContext>
      </div>
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          {/* Backdrop click to close */}
          <div
            className="absolute inset-0"
            onClick={() => setShowConfirmModal(false)}
          />

          {/* Modal content */}
          <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              Confirm Completion
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Mark this shipment as completed? This action cannot be undone.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setSelectedShipmentId(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!selectedShipmentId) return;
                  setShowConfirmModal(false);

                  const backup = [...activeData];
                  setActiveData((prev) =>
                    prev.filter((r) => r._id !== selectedShipmentId),
                  );

                  try {
                    await completeShippingSchedule(selectedShipmentId);
                    const updatedCompleted =
                      await getCompletedShippingSchedules();
                    setCompletedData(
                      updatedCompleted.sort(
                        (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt),
                      ),
                    );
                    toast.success("Shipment marked as completed!");
                  } catch (err) {
                    setActiveData(backup);
                    toast.error("Failed to complete shipment");
                  }

                  setSelectedShipmentId(null);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                Mark as complete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerShippingSchedule;
